import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { track } from '../analytics';
import type { Challenge, ChallengeStake, ChallengeType, PassportTab } from '../types/passport';

interface UseChallengesDataOptions {
  effectiveToken: string | null;
  effectiveCustomerId: string | undefined;
  passportTab: PassportTab;
}

export function useChallengesData(options: UseChallengesDataOptions) {
  const { effectiveToken, effectiveCustomerId, passportTab } = options;

  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [challengesLoading, setChallengesLoading] = useState(false);
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);

  const fetchChallenges = useCallback(async () => {
    if (!effectiveToken) return;
    setChallengesLoading(true);
    try {
      const { data: cd } = await axios.get(`${API_URL || ''}/api/challenges`, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });
      setChallenges(cd);
    } catch { /* */ }
    finally { setChallengesLoading(false); }
  }, [effectiveToken]);

  useEffect(() => {
    if (passportTab === 'challenges' && effectiveToken) {
      track('challenge_viewed', { pendingCount: challenges.filter(c => c.status === 'PENDING').length });
      if (challenges.length === 0) fetchChallenges();
    }
  }, [passportTab, challenges, effectiveToken, fetchChallenges]);

  const handleAcceptChallenge = async (challengeId: string, stake: ChallengeStake) => {
    if (!effectiveToken) return;
    try {
      await axios.post(`${API_URL || ''}/api/challenges/${challengeId}/accept`,
        { opponentStake: stake },
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      track('challenge_joined', { challengeId, stake });
      await fetchChallenges();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Error');
    }
  };

  const handleDeclineChallenge = async (challengeId: string) => {
    if (!effectiveToken) return;
    try {
      await axios.post(`${API_URL || ''}/api/challenges/${challengeId}/decline`, {},
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      await fetchChallenges();
    } catch { /* */ }
  };

  const handleCancelChallenge = async (challengeId: string) => {
    if (!effectiveToken) return;
    try {
      await axios.post(`${API_URL || ''}/api/challenges/${challengeId}/cancel`, {},
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      await fetchChallenges();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Error');
    }
  };

  const handleCreateChallenge = async (payload: {
    opponentId: string;
    type: ChallengeType;
    deadlineDays: number;
    initiatorStake: ChallengeStake;
  }) => {
    if (!effectiveToken) return;
    try {
      await axios.post(`${API_URL || ''}/api/challenges`, payload,
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      setShowCreateChallenge(false);
      await fetchChallenges();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Error');
    }
  };

  const pendingChallengeCount = challenges.filter(
    c => c.status === 'PENDING' && c.opponentId === effectiveCustomerId
  ).length;

  return {
    challenges,
    challengesLoading,
    showCreateChallenge,
    setShowCreateChallenge,
    pendingChallengeCount,
    fetchChallenges,
    handleAcceptChallenge,
    handleDeclineChallenge,
    handleCancelChallenge,
    handleCreateChallenge,
  };
}
